<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@youcan/celeste';
import { useNavItems } from '@/composables/use-nav-items';

const page = usePage();
const { items } = useNavItems();
const { logout } = useAuthStore();
const bp = useBreakpoints(breakpointsTailwind);

const isMobile = bp.smaller('lg');
</script>

<template>
  <div class="header-wrapper" role="presentation">
    <header class="container xl:max-w-screen-xl">
      <div class="flex gap-[16px] items-center">
        <template v-if="isMobile">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button
                intent="neutral"
                icon-position="left"
                size="sm"
                variant="stroke"
              >
                <i i-celeste-menu-line />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  v-for="item in items"
                  :key="item.label"
                  :as="Link"
                  :href="route(item.route)"
                >
                  <template #prefix>
                    <i :class="item.icon" />
                  </template>
                  {{ item.label }}
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </template>

        <Logo class="logo" />
      </div>

      <div class="end">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button
              intent="neutral"
              icon-position="left"
              size="sm"
              type="button"
              variant="stroke"
            >
              <i i-celeste-user-6-line />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            <DropdownMenuGroup>
              <div role="presentation" class="p-y-[8px] flex gap-[8px] items-center">
                <div aria-hidden class="user-icon">
                  <i i-celeste-user-6-line />
                </div>

                <div class="user-info">
                  <div class="user-name">
                    {{ page.props.user?.first_name }} {{ page.props.user?.last_name }}
                  </div>

                  <div class="user-email">
                    {{ page.props.user?.email }}
                  </div>
                </div>
              </div>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuGroup>
              <DropdownMenuItem @click="logout">
                <template #prefix>
                  <i flip-icon i-celeste-logout-circle-line />
                </template>

                {{ $t('common.form.action.logout') }}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  </div>
</template>

<style scoped>
.header-wrapper {
  z-index: 50;
  border-bottom: 1px solid var(--color-stroke-soft-200);
  background-color: var(--color-bg-white-0);
}

.header-wrapper * {
  user-select: none;
}

header {
  display: flex;
  align-items: center;
  height: var(--topbar-height);
  margin: 0 auto;
  padding: 20px 0;
  gap: 24px;
}

@screen lt-lg {
  header {
    padding: 0 4%;
  }
}

:deep(.logo) {
  height: 20px;
}

.end {
  gap: var(--spacing-8);
  display: flex;
  margin-left: auto;
}

.user-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--color-stroke-sub-300);
  border-radius: 96px;
  background: var(--color-bg-white-0);
  box-shadow: var(--shadow-regular-xs);
  gap: 12px;
}

.user-icon i {
  color: var(--color-icon-soft-400);
}

.user-email {
  overflow: hidden;
  color: var(--color-text-soft-400);
  font: var(--paragraph-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-name {
  overflow: hidden;
  color: var(--color-text-strong-950);
  font: var(--label-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
